/**
 * Backup Cost & Controls API client (Spec 256 — Backup Cost Governance).
 * Read surfaces for the Cost & Controls tab. Non-currency by contract (FR-022).
 * Author: Peter Dobler (Dobler Data Solutions)
 */

import apiClient from '../services/api';

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup/cost';

export type ControlSource = 'global' | 'override' | 'plan' | 'default';
export interface ControlState {
  effective: boolean;
  source: ControlSource;
}

export interface PlanStatusRow {
  planId: string;
  name: string;
  worm: ControlState;
  verification: ControlState;
  storageTarget: string | null;
  regionAxis: 'in-region' | 'cross-region' | 'unknown';
  pathAxis: 'server-side' | 'private-endpoint' | 'nat-internet' | 'unknown';
  routeClass: string;
  lockedPoints: number;
  earliestUnlockAt: string | null;
}

export interface CostStatus {
  fleet: {
    total: number;
    worm: { on: number; total: number };
    verification: { on: number; total: number };
    storageTargets: number;
    crossRegion: number;
    unknownRoute: number;
    lockedPoints: number;
  };
  globals: { worm: boolean; verification: boolean };
  plans: PlanStatusRow[];
}

// 🛑 Spec 270 FR-019a: 'secondary' removed. The backend no longer accepts it (400), and the
// column it wrote is dropped by migration 280.
export type ControlDomain = 'worm' | 'verification';

export interface FleetControlSummary {
  total: number;
  worm: { on: number; total: number };
  verification: { on: number; total: number };
}

export interface ReclaimCandidate {
  copyId: string;
  entityType: string;
  entityId: string;
  isVault: boolean;
  bytesMoved: number | null;
  planId: string;
  planName: string;
  wormRetained: boolean;
  earliestUnlockAt: string | null;
}

export interface ReclaimPreview {
  reclaimable: ReclaimCandidate[];
  retained: ReclaimCandidate[];
  totals: { reclaimableCopies: number; reclaimableBytes: number | null; retainedCopies: number };
}

export interface ReclaimResult {
  requested: number;
  reclaimed: number;
  retained: number;
  failed: number;
  freedBytes: number | null;
}

export type OffenderGrouping = 'plan' | 'entity';
export interface Offender {
  key: string;
  name: string;
  bytes: number;
  sharePct: number;
  routeClass: string;
  basis: string;
}
export interface OffendersResult {
  windowDays: number;
  by: OffenderGrouping;
  totalBytes: number;
  measured: boolean;
  offenders: Offender[];
  residual: { bytes: number; label: string } | null;
}

export type SuggestionPriority = 'high' | 'medium' | 'low';
export interface CostSuggestion {
  priority: SuggestionPriority;
  category: string;
  current: string;
  change: string;
  benefit: string;
}
export interface AdvisorResult {
  configScore: { current: number; bestCasePct: number };
  suggestions: CostSuggestion[];
}

export const costApi = {
  /** GET /cost/status — fleet + per-plan control/route roll-up (side-effect-free). */
  getStatus: async (): Promise<CostStatus> =>
    (await apiClient.get<Wrapped<CostStatus>>(`${BASE}/status`)).data,

  /** PUT /cost/controls/global/:domain — flip a GLOBAL control default (US2). WORM = Admin only. */
  setGlobalControl: async (domain: ControlDomain, value: boolean): Promise<FleetControlSummary> =>
    (await apiClient.put<Wrapped<FleetControlSummary>>(`${BASE}/controls/global/${domain}`, { value })).data,

  /** PUT /cost/plans/:planId/controls/:domain — set a PER-PLAN override (US2). value=null → inherit. */
  setPlanControl: async (
    planId: string,
    domain: ControlDomain,
    value: boolean | null,
  ): Promise<ControlState> =>
    (await apiClient.put<Wrapped<ControlState>>(`${BASE}/plans/${planId}/controls/${domain}`, { value })).data,

  /** GET /cost/reclaim/preview — orphaned copies split reclaimable vs WORM-retained (Admin). */
  reclaimPreview: async (): Promise<ReclaimPreview> =>
    (await apiClient.get<Wrapped<ReclaimPreview>>(`${BASE}/reclaim/preview`)).data,

  /** POST /cost/reclaim — hard-delete the selected reclaimable copies (Admin). */
  reclaim: async (copyIds: string[]): Promise<ReclaimResult> =>
    (await apiClient.post<Wrapped<ReclaimResult>>(`${BASE}/reclaim`, { copyIds })).data,

  /** GET /cost/advisor — prioritized cost-optimization suggestions (advisory, non-currency). */
  advisor: async (): Promise<AdvisorResult> =>
    (await apiClient.get<Wrapped<AdvisorResult>>(`${BASE}/advisor`)).data,

  /** GET /cost/offenders — egress/transfer offenders over a window, by plan or entity. */
  offenders: async (windowDays = 30, by: OffenderGrouping = 'plan'): Promise<OffendersResult> =>
    (await apiClient.get<Wrapped<OffendersResult>>(`${BASE}/offenders?window=${windowDays}d&by=${by}`)).data,
};
